import React, { useState, useRef } from 'react';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { color } from 'theme';
import { useTeamColors } from 'hooks';
import {
  FootballIcon,
  BasketballIcon,
  BaseballIcon,
  SoccerIcon,
  HockeyIcon,
  TennisIcon,
  GolfIcon,
  HorseRacingIcon,
} from './SportIcons';
import { getLeagueSport, SPORT_TYPES } from './leagueToSport';

// Get sport icon component based on sport type
const getSportIcon = (sportType, altColor) => {
  if (!sportType) return null;

  const iconProps = { color: altColor, size: 16 };

  switch (sportType) {
    case SPORT_TYPES.FOOTBALL:
      return <FootballIcon {...iconProps} />;
    case SPORT_TYPES.BASKETBALL:
      return <BasketballIcon {...iconProps} />;
    case SPORT_TYPES.BASEBALL:
      return <BaseballIcon {...iconProps} />;
    case SPORT_TYPES.SOCCER:
      return <SoccerIcon {...iconProps} />;
    case SPORT_TYPES.HOCKEY:
      return <HockeyIcon {...iconProps} />;
    case SPORT_TYPES.TENNIS:
      return <TennisIcon {...iconProps} />;
    case SPORT_TYPES.GOLF:
      return <GolfIcon {...iconProps} />;
    case SPORT_TYPES.HORSE_RACING:
      return <HorseRacingIcon {...iconProps} />;
    default:
      return null;
  }
};

// Custom Option component with sport icons
const CustomOption = (props) => {
  const { data, selectProps } = props;
  // For favorites, use the league property; otherwise use the label
  const leagueOrLabel = data.league || data.label;
  const sportType = getLeagueSport(leagueOrLabel);
  const icon = getSportIcon(sportType, selectProps.altColor);
  // Use displayLabel if available (for favorites), otherwise use label
  const displayText = data.displayLabel || data.label;

  return (
    <components.Option {...props}>
      <div
        style={{ display: 'flex', alignItems: 'center' }}
        title={data.disabledHint}
      >
        {icon}
        <span>{displayText}</span>
      </div>
    </components.Option>
  );
};

// Custom SingleValue component with sport icons (for selected value)
const CustomSingleValue = (props) => {
  const { data, selectProps } = props;
  // For favorites, use the league property; otherwise use the label
  const leagueOrLabel = data.league || data.label;
  const sportType = getLeagueSport(leagueOrLabel);
  const icon = getSportIcon(sportType, selectProps.altColor);
  // Use displayLabel if available (for favorites), otherwise use label
  const displayText = data.displayLabel || data.label;

  return (
    <components.SingleValue {...props}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <span>{displayText}</span>
      </div>
    </components.SingleValue>
  );
};

// Custom DropdownIndicator with team color
const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        height="20"
        width="20"
        viewBox="0 0 20 20"
        aria-hidden="true"
        focusable="false"
        style={{ fill: props.selectProps.accentColor }}
      >
        <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"></path>
      </svg>
    </components.DropdownIndicator>
  );
};

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  padding: 2px 10px 2px 10px;
  outline: none !important;
`;

const createSelectStyles = (accentColor, altColor) => ({
  control: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: 'transparent',
    border: 'transparent',
    borderBottom: `1px solid ${accentColor}`,
    borderRadius: 'none',
    maxWidth: '250px',
    minWidth: '200px',
    width: '50%',
    color: '#ffffff',
    marginBottom: 'none',
    cursor: 'pointer',
    outline: 'none !important',
    boxShadow: 'none',
    opacity: isDisabled ? 0.5 : 1,
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    border: `2px solid ${altColor}`,
    borderRadius: '8px',
  }),
  option: (styles, { isFocused, isSelected, isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#777777' : isSelected ? '#000000' : '#ffffff',
    backgroundColor:
      !isDisabled && (isFocused || isSelected) ? accentColor : 'transparent',
    pointerEvents: isSelected ? 'none' : 'auto',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: '#ffffff',
  }),
});

const selectorColors = {
  primary: 'transparent',
  primmary25: 'transparent',
};

const SelectTitles = styled.span`
  color: ${color.greyText};
  font-size: 1.8rem;
  line-height: 2.4rem;
  margin-right: 1rem;
  font-weight: 300;
`;

export const Selector = ({
  isDisabled = false,
  placeholder = 'Select',
  options,
  //defaultValue,
  onChange,
  value,
  title = '',
}) => {
  const [isSearch, setIsSearch] = useState(true);
  const { mainColor, altColor } = useTeamColors();
  const selectStyles = createSelectStyles(mainColor, altColor);

  const ref = useRef();

  function handleOnFocus(thisRef) {
    setTimeout(() => {
      if (!thisRef.current) return;

      const { focusedOptionRef } = thisRef.current;
      let selectedNode = focusedOptionRef;

      if (focusedOptionRef) {
        if (value) {
          if (value.label != focusedOptionRef.innerHTML) {
            let parentElement = focusedOptionRef.parentElement;

            parentElement.childNodes.forEach((node) => {
              if (value.label == node.innerHTML) {
                selectedNode = node;
              }
            });
          }
        }
      }

      // Scroll within the dropdown menu only, not the entire page
      // Use scrollIntoView with block: 'nearest' to prevent page scrolling
      if (selectedNode) {
        selectedNode.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }, 1);
  }

  /*
  function getSelectValue() {
    setTimeout(() => {
      value;
    }, 1);
  }
    */

  // function handleMenuOpen(thisRef) {
  //   setTimeout(() => {
  //     console.log(thisRef.current);
  //     const { focusedOptionRef } = thisRef.current;
  //     focusedOptionRef &&
  //       focusedOptionRef.scrollIntoView({ behavior: 'smooth' });
  //   }, 0.1);

  //   //setIsSearch(true);
  // }

  return (
    <StyledLabel>
      {title ? <SelectTitles>Team</SelectTitles> : null}
      <Select
        accentColor={mainColor}
        altColor={altColor}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        blurInputOnSelect={true}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: DropdownIndicator,
          Option: CustomOption,
          SingleValue: CustomSingleValue,
        }}
        isDisabled={isDisabled}
        isSearchable={isSearch}
        menuShouldBlockScroll={false}
        menuShouldScrollIntoView={false}
        onChange={onChange}
        onFocus={() => handleOnFocus(ref)}
        onMenuClose={() => setIsSearch(false)}
        onMenuOpen={() => setIsSearch(true)}
        //onMenuOpen={() => handleMenuOpen(ref)}
        options={options}
        placeholder={placeholder}
        ref={ref}
        styles={selectStyles}
        theme={{
          colors: selectorColors,
        }}
        value={value}
        //value={getSelectValue()}
      />
    </StyledLabel>
  );
};
